import { Collection } from "@authory-packages/core";
import { CCPCollectionBadge, CCPCollectionsWrapper } from "./ContentCollectionsPanel.styles";
import { V3CaptionGrey35 } from "../ReusableStyledComponents/V3CaptionGrey35";
import { V3IconClose } from "../Icons";
import { V3Dropdown } from "../Dropdown";
import { AddToCollectionDropdownPanel } from "../AddToCollectionDropdownPanel";
import { APPLICATION_ROUTES } from "../../shared-logic/routes";
import { UnstyledInlineLink } from "../ReusableStyledComponents/UnstyledLink";
import { V3BlueRoundPlus } from "../ReusableStyledComponents/V3BlueRoundPlus";
import { V3Tooltip } from "../Tooltip";
import { V3TertiaryButton } from "../Button";
import React from "react";
import { useWindowSize } from "../../hooks/useWindowSize";
import { theme } from "../../theme";

const ColLabel = ({ label }: { label: string }) => <V3CaptionGrey35>{label}</V3CaptionGrey35>;

interface ContentCollectionsPanelProps {
    allCollections: Collection[];
    contentCollections: Collection[];
    isPlugin?: boolean;
    isInfoBadgeOnly?: boolean;
    modifyItemOnCollectionCallback: (add: boolean, collectionSlug: string) => void;
}

export const ContentCollectionsPanel = ({
    allCollections,
    contentCollections,
    isPlugin = false,
    isInfoBadgeOnly = false,
    modifyItemOnCollectionCallback
}: ContentCollectionsPanelProps) => {
    const collectionToAdd = allCollections.filter((cl) => !contentCollections.find((ccl) => ccl.canonicalSlug === cl.canonicalSlug));
    const UseLink = isPlugin ? React.Fragment : UnstyledInlineLink;
    const size = useWindowSize();
    const IS_MOBILE = (size.width && size.width < theme.v3.layout.sidebarLayout.mobile) || false;
    const NO_TAGS_TO_SHOW = IS_MOBILE ? 1 : 3;

    const firstHalf = !isInfoBadgeOnly ? contentCollections : contentCollections.slice(0, NO_TAGS_TO_SHOW);
    const secondHalf = !isInfoBadgeOnly ? [] : contentCollections.slice(NO_TAGS_TO_SHOW);

    return (
        <CCPCollectionsWrapper>
            {firstHalf.map((col) => {
                const NameColLabel = () => <ColLabel label={col.label} />;

                return (
                    <React.Fragment key={col.canonicalSlug}>
                        <CCPCollectionBadge $noCta={isPlugin} $isInfoBadgeOnly={isInfoBadgeOnly}>
                            <div>
                                <V3Tooltip
                                    referenceContent={
                                        <>
                                            {isPlugin || isInfoBadgeOnly ? (
                                                <NameColLabel />
                                            ) : (
                                                <UseLink href={`${APPLICATION_ROUTES.COLLECTION}/${col.canonicalSlug}`}>
                                                    <NameColLabel />
                                                </UseLink>
                                            )}
                                        </>
                                    }
                                    tooltipContent={isInfoBadgeOnly ? "Content item is in this collection" : "Go to the collection"}
                                />
                            </div>
                            {!isInfoBadgeOnly && (
                                <div
                                    onClick={() => {
                                        modifyItemOnCollectionCallback(false, col.canonicalSlug);
                                    }}
                                >
                                    <V3Tooltip
                                        referenceContent={<V3IconClose width="10" height="10" strokeWidth="4" />}
                                        tooltipContent={"Remove from collection"}
                                    />
                                </div>
                            )}
                        </CCPCollectionBadge>
                    </React.Fragment>
                );
            })}
            {!!secondHalf.length && (
                <>
                    <CCPCollectionBadge $noCta $isInfoBadgeOnly>
                        <div>
                            <V3Tooltip
                                referenceContent={
                                    <>
                                        <ColLabel label={`+ ${secondHalf.length} more`} />
                                    </>
                                }
                                tooltipContent={
                                    <>
                                        <label style={{ marginBottom: 8, display: "block" }}>Content item is also in these collections:</label>
                                        {secondHalf.map((it) => (
                                            <React.Fragment key={it.canonicalSlug}>
                                                {it.label}
                                                <br />
                                            </React.Fragment>
                                        ))}
                                    </>
                                }
                            />
                        </div>
                    </CCPCollectionBadge>
                </>
            )}
            {!!collectionToAdd.length && (
                <div style={{ margin: "5px 6px", lineHeight: 0 }}>
                    <V3Dropdown
                        title="Add to collection"
                        strategy="fixed"
                        placement="bottom-end"
                        shiftPadding={80}
                        renderPopperElement={(_, setOpen) => {
                            return (
                                <AddToCollectionDropdownPanel
                                    collections={collectionToAdd}
                                    onCollectionClick={(collectionSlug) => {
                                        modifyItemOnCollectionCallback(true, collectionSlug);
                                        setOpen(false);
                                    }}
                                />
                            );
                        }}
                        renderReferenceElement={(open) => {
                            const Elem = () => <V3TertiaryButton icon={<V3BlueRoundPlus />} text={"Add to collection"} type="button" />;

                            if (open) {
                                return <Elem />;
                            }

                            return <V3Tooltip referenceContent={<Elem />} tooltipContent={"Add to collection"} />;
                        }}
                    />
                </div>
            )}
        </CCPCollectionsWrapper>
    );
};
